import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import type { QueryData } from "@supabase/supabase-js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export type RuleSpecialty = Database["public"]["Tables"]["icd_10_code_rule_specialties"]["Row"]["specialty"];

export const RULE_CONFLICT_MESSAGE =
  "This rule was changed by someone else. Close this dialog, reopen the rule and try again.";

export const RULE_NOT_FOUND_MESSAGE = "This rule no longer exists or you no longer have access to it.";

type SaveRuleArgs = Database["public"]["Functions"]["save_icd_10_code_rule"]["Args"];

export type UpsertRuleData = {
  name: string;
  rule_text: string;
  is_active: boolean;
  codeIds: string[];
  customerIds: string[];
  specialties: RuleSpecialty[];
  expectedUpdatedAt?: string;
};

const getIcd10CodeRuleQuery = () => {
  return supabase
    .from("icd_10_code_rules")
    .select(
      `
      *,
      icd10CodeRuleAssignments:icd_10_code_rule_assignments!inner(
        *,
        icd10Code:icd10_codes!inner(
          id,
          code,
          description
        )
      ),
      icd10CodeRuleCustomers:icd_10_code_rule_customers(*),
      icd10CodeRuleSpecialties:icd_10_code_rule_specialties(*)
    `,
    )
    .order("created_at", { ascending: false });
};

export type Icd10CodeRule = QueryData<ReturnType<typeof getIcd10CodeRuleQuery>>[number];

export const useIcd10CodeRules = ({
  searchQuery,
  codeSearchQuery,
}: {
  searchQuery?: string;
  codeSearchQuery?: string;
}) => {
  const [rules, setRules] = useState<Icd10CodeRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch initial data and on filter change
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(null);

    const fetchRules = async () => {
      try {
        let query = getIcd10CodeRuleQuery().order("name");

        if (searchQuery) {
          query = query.or(`name.ilike.%${searchQuery}%,rule_text.ilike.%${searchQuery}%`);
        }

        if (codeSearchQuery) {
          // First, find codes that match the search
          const { data: matchingCodes, error: codeError } = await supabase
            .from("icd10_codes")
            .select("id")
            .ilike("code", `%${codeSearchQuery}%`);

          if (codeError) {
            throw codeError;
          }

          if (matchingCodes && matchingCodes.length > 0) {
            const codeIds = matchingCodes.map((code) => code.id);

            // Then filter rules by those code IDs
            query = query.in("icd10CodeRuleAssignments.code_id", codeIds);
          } else {
            // No matching codes found, return empty result
            setRules([]);
            setIsLoading(false);
            return;
          }
        }

        const { data, error } = await query;

        if (!isMounted) return;
        if (error) {
          setError(error);
          setIsLoading(false);
          toast.error("Failed to load ICD-10 code rules. Please refresh the page.");
        } else {
          setRules(data);
          setIsLoading(false);
        }
      } catch (error) {
        if (!isMounted) return;
        setError(error as Error);
        setIsLoading(false);
        toast.error("Failed to load ICD-10 code rules. Please refresh the page.");
      }
    };

    fetchRules();

    return () => {
      isMounted = false;
    };
  }, [searchQuery, codeSearchQuery]);

  // Subscribe to realtime changes
  useEffect(() => {
    const channel = supabase
      .channel("icd_10_code_rules-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "icd_10_code_rules",
        },
        async (payload) => {
          const { eventType, new: newRow, old: oldRow } = payload;
          if (eventType === "DELETE") {
            setRules((prev) => {
              return prev.filter((rule) => rule.id !== oldRow.id);
            });
          } else {
            const { data: newEnrichedRule } = await getIcd10CodeRuleQuery().eq("id", newRow.id).single();

            if (newEnrichedRule) {
              if (eventType === "UPDATE") {
                setRules((prev) => {
                  return prev.map((rule) => (rule.id === newRow.id ? { ...rule, ...newEnrichedRule } : rule));
                });
              } else {
                setRules((prev) => [newEnrichedRule, ...prev]);
              }
            }
          }
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const toggleRuleActive = async (ruleId: string, currentIsActive: boolean) => {
    try {
      const { error } = await supabase
        .from("icd_10_code_rules")
        .update({ is_active: !currentIsActive })
        .eq("id", ruleId);

      if (error) {
        toast.error("Failed to update rule status. Please try again.");
        throw error;
      }

      // Optimistically update the local state
      setRules((prev) => prev.map((rule) => (rule.id === ruleId ? { ...rule, is_active: !currentIsActive } : rule)));

      toast.success(`Rule ${!currentIsActive ? "activated" : "deactivated"} successfully.`);
    } catch (error) {
      console.error("Error toggling rule active status:", error);
    }
  };

  const _queryClient = useQueryClient();

  const upsertRuleMutation = useMutation({
    mutationFn: async ({ ruleData, ruleId }: { ruleData: UpsertRuleData; ruleId?: string }) => {
      if (!ruleData.name.trim() || !ruleData.rule_text.trim()) {
        throw new Error("Name and rule text are required");
      }

      if (ruleData.codeIds.length === 0) {
        throw new Error("At least one ICD-10 code must be selected");
      }

      if (ruleId && !ruleData.expectedUpdatedAt) {
        throw new Error("expectedUpdatedAt is required to edit a rule");
      }

      // The generated types call the nullable arguments required strings; null means create / no version check
      const args = {
        p_rule_id: (ruleId ?? null) as string,
        p_expected_updated_at: (ruleData.expectedUpdatedAt ?? null) as string,
        p_name: ruleData.name.trim(),
        p_rule_text: ruleData.rule_text.trim(),
        p_is_active: ruleData.is_active,
        p_code_ids: ruleData.codeIds,
        p_customer_ids: ruleData.customerIds,
        p_specialties: ruleData.specialties,
      } satisfies SaveRuleArgs;

      const { data: savedRuleId, error: saveError } = await supabase.rpc("save_icd_10_code_rule", args);

      if (saveError?.code === "PT409") throw new Error(RULE_CONFLICT_MESSAGE);
      if (saveError?.code === "PT404") throw new Error(RULE_NOT_FOUND_MESSAGE);
      if (saveError) throw saveError;

      const { data: enrichedRule } = await getIcd10CodeRuleQuery().eq("id", savedRuleId).single();

      if (enrichedRule) {
        setRules((prev) =>
          ruleId ? prev.map((rule) => (rule.id === ruleId ? enrichedRule : rule)) : [enrichedRule, ...prev],
        );
      }

      toast.success(ruleId ? "Rule updated successfully" : "Rule created successfully");
      return enrichedRule;
    },
    onError: (error: Error) => {
      console.error("Error upserting rule:", error);
      toast.error(error.message || "Failed to upsert rule. Please try again.");
    },
  });

  const deleteRuleMutation = useMutation({
    mutationFn: async (ruleId: string) => {
      // Delete assignments first
      await supabase.from("icd_10_code_rule_assignments").delete().eq("rule_id", ruleId);

      // Delete the rule
      const { error } = await supabase.from("icd_10_code_rules").delete().eq("id", ruleId);

      if (error) throw error;

      // Update local state
      setRules((prev) => prev.filter((rule) => rule.id !== ruleId));

      toast.success("Rule deleted successfully");
    },
    onError: (error: Error) => {
      console.error("Error deleting rule:", error);
      toast.error("Failed to delete rule. Please try again.");
    },
  });

  return {
    data: rules,
    isLoading,
    error,
    toggleRuleActive,
    upsertRuleMutation,
    deleteRuleMutation,
  };
};
